import React, { useState, useCallback, useEffect, useRef } from "react";
import { format, parse, isValid } from "date-fns";
import { mdiCalendar, mdiClose } from "@mdi/js";
import { cn } from "../../styles";
import { LocalizationScope } from "../../util/LocalizationScope";
import { Button, ButtonVariant, Typography } from "../../atoms";
import { ThemeColor } from "../../props";
import { processErrorContent } from "./helper";
import { Input } from "../Input";
import { BaseFormFieldProps, FormFieldConstraint } from "./index";
import { Calendar } from "../Calendar";
import { Dialog } from "../../layouts";

export interface DateTimeFieldConstraint extends FormFieldConstraint {
  datetime?: {
    earliest?: Date;
    latest?: Date;
  };
}

export interface FormFieldDateTimeProps extends Omit<
  BaseFormFieldProps,
  "constraints"
> {
  value: Date | null;
  data?: { input?: string };
  onChangeField: (
    id: string,
    value: Date | null,
    opts?: { data?: { input?: string } },
  ) => void;
  constraints: DateTimeFieldConstraint;
  /** date-fns format string, e.g. "dd.MM.yyyy" or "dd.MM.yyyy HH:mm" */
  format: string;
  /** Hide time selectors in the calendar picker. Defaults to whether the format has time tokens. */
  dateOnly?: boolean;
  placeholder?: string;
}

function formatToMask(fmt: string): string {
  return fmt.replace(/[yMdHhms]/g, "9");
}

type SegmentInfo = { start: number; end: number; min: number; max: number };

const TOKEN_MAP: Record<string, { min: number; max: number }> = {
  dd: { min: 1, max: 31 },
  MM: { min: 1, max: 12 },
  HH: { min: 0, max: 23 },
  hh: { min: 1, max: 12 },
  mm: { min: 0, max: 59 },
  ss: { min: 0, max: 59 },
};

function getSegmentInfos(fmt: string): SegmentInfo[] {
  const segments: SegmentInfo[] = [];
  let pos = 0;
  let i = 0;
  while (i < fmt.length) {
    let matched = false;
    for (const token of Object.keys(TOKEN_MAP)) {
      if (fmt.startsWith(token, i)) {
        segments.push({
          start: pos,
          end: pos + token.length,
          ...TOKEN_MAP[token],
        });
        pos += token.length;
        i += token.length;
        matched = true;
        break;
      }
    }
    if (!matched) {
      pos++;
      i++;
    }
  }
  return segments;
}

export const DateTime: React.FC<FormFieldDateTimeProps & { type?: string }> = ({
  className,
  id,
  value,
  data,
  onChangeField,
  label = "Label",
  localKey,
  errors = [],
  touched,
  helper,
  helperKey,
  constraints,
  format: dateFormat,
  type,
  dateOnly,
  placeholder,
  disabled,
  keepHeight,
  noLabel,
  dense,
  readOnly,
}) => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogValue, setDialogValue] = useState<Date | null>(null);

  const resolvedFormat =
    dateFormat ?? (type === "datetime" ? "dd.MM.yyyy HH:mm" : "dd.MM.yyyy");
  const error = touched && errors[0];
  const mask = formatToMask(resolvedFormat);

  // Time selectors only make sense when the format carries time (H/h/m/s, ignoring quoted literals)
  const resolvedDateOnly =
    dateOnly ?? !/[Hhms]/.test(resolvedFormat.replace(/'[^']*'/g, ""));

  const inputStr =
    data?.input ??
    (value && isValid(value) ? format(value, resolvedFormat) : "");
  const showClear =
    !disabled && !readOnly && !constraints.presence && !!inputStr;

  // Initialise data.input from value on first mount when not yet set
  const mountedRef = useRef(false);
  useEffect(() => {
    if (!mountedRef.current) {
      mountedRef.current = true;
      if (value && isValid(value) && !data?.input) {
        (onChangeField as any)(id, value, {
          data: { input: format(value, resolvedFormat) },
          disableCallback: true,
        });
      }
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleInputChange = useCallback(
    (raw: string) => {
      const parsed =
        raw && !raw.includes("_")
          ? parse(raw, resolvedFormat, new Date())
          : null;
      const date = parsed && isValid(parsed) ? parsed : null;
      onChangeField(id, date, { data: { input: raw } });
    },
    [resolvedFormat, id, onChangeField],
  );

  const handleBeforeMaskedStateChange = useCallback(
    ({
      currentState,
      previousState,
      nextState,
    }: {
      currentState?: {
        value: string;
        selection: { start: number | null; end: number | null };
      };
      previousState?: {
        value: string;
        selection: { start: number | null; end: number | null };
      };
      nextState: {
        value: string;
        selection: { start: number | null; end: number | null };
      };
    }) => {
      const prev = previousState ?? currentState ?? nextState;
      const { value: nextValue } = nextState;
      for (const seg of getSegmentInfos(resolvedFormat)) {
        const segValue = nextValue.slice(seg.start, seg.end);
        if (!segValue.includes("_")) {
          const num = parseInt(segValue, 10);
          if (isNaN(num) || num < seg.min || num > seg.max) return prev;
        } else if (segValue[0] !== "_") {
          if (parseInt(segValue[0], 10) * 10 > seg.max) return prev;
        }
      }
      return nextState;
    },
    [resolvedFormat],
  );

  const handleClear = () => onChangeField(id, null, { data: { input: "" } });

  const handleOpenDialog = () => {
    setDialogValue(value && isValid(value) ? value : new Date());
    setDialogOpen(true);
  };

  const handleDialogOk = () => {
    if (dialogValue && isValid(dialogValue)) {
      onChangeField(id, dialogValue, {
        data: { input: format(dialogValue, resolvedFormat) },
      });
    }
    setDialogOpen(false);
  };

  const handleDialogClear = () => {
    onChangeField(id, null, { data: { input: "" } });
    setDialogOpen(false);
  };

  return (
    <div className={className}>
      {!noLabel && (
        <div className="flex">
          <Typography.Label
            htmlFor={id}
            className={cn(
              "block font-medium select-none",
              dense ? "mb-0.5 text-xs" : "mb-1 text-sm",
            )}
            content={label || { local: id }}
          />
          {!!constraints.presence && (
            <span className="text-red-500 ml-0.5 text-xs align-super">*</span>
          )}
        </div>
      )}
      <div className="flex gap-1 items-center">
        <Input
          className="grow"
          id={id}
          value={inputStr}
          onChange={handleInputChange}
          placeholder={placeholder ?? resolvedFormat}
          disabled={disabled}
          readOnly={readOnly}
          required={!!constraints.presence}
          dense={dense}
          error={!!error}
          mask={mask}
          beforeMaskedStateChange={handleBeforeMaskedStateChange}
          aria-invalid={!!error}
          aria-describedby={
            helper ? `${id}-helper` : error ? `${id}-error` : undefined
          }
        />
        <Button
          variant={ButtonVariant.text}
          color={ThemeColor.gray}
          onClick={handleOpenDialog}
          iconPath={mdiCalendar}
          dense
          muted
          tabIndex={-1}
          disabled={disabled || readOnly}
        />
        {showClear && (
          <Button
            variant={ButtonVariant.text}
            color={ThemeColor.gray}
            onClick={handleClear}
            iconPath={mdiClose}
            dense
            muted
            tabIndex={-1}
            disabled={disabled}
          />
        )}
      </div>
      {(helperKey || helper || keepHeight) && !error && (
        <LocalizationScope scope={localKey}>
          <Typography.P
            id={`${id}-helper`}
            className={cn("text-xs", dense ? "my-0.5" : "my-1")}
            color={ThemeColor.gray}
            content={helperKey ? { local: helperKey } : helper ? helper : null}
          >
            {" "}
          </Typography.P>
        </LocalizationScope>
      )}
      {error && (
        <LocalizationScope scope={[localKey, "::PSS"]}>
          <Typography.P
            id={`${id}-error`}
            className={cn("text-xs", dense ? "my-0.5" : "my-1")}
            color={ThemeColor.error}
            content={processErrorContent(error)}
          />
        </LocalizationScope>
      )}

      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        minWidth={0}
      >
        <Dialog.Content scroll={false} className="!p-0 !overflow-hidden">
          <Calendar
            value={dialogValue ?? undefined}
            onChange={setDialogValue}
            dateOnly={resolvedDateOnly}
            minValue={constraints.datetime?.earliest}
            maxValue={constraints.datetime?.latest}
          />
        </Dialog.Content>
        <Dialog.Footer>
          <Dialog.Actions>
            <Button
              variant={ButtonVariant.text}
              color={ThemeColor.gray}
              onClick={handleDialogClear}
              dense
            >
              <Typography.Span content={{ local: "Clear" }} />
            </Button>
            <Button
              variant={ButtonVariant.text}
              color={ThemeColor.gray}
              onClick={() => setDialogOpen(false)}
              dense
            >
              <Typography.Span content={{ local: "Cancel" }} />
            </Button>
            <Button
              variant={ButtonVariant.contained}
              color={ThemeColor.primary}
              onClick={handleDialogOk}
              dense
            >
              <Typography.Span content={{ local: "OK" }} />
            </Button>
          </Dialog.Actions>
        </Dialog.Footer>
      </Dialog>
    </div>
  );
};

DateTime.displayName = "FormField.DateTime";
